import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatRupiah, KATEGORI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Plus, Minus, Search, Trash2, ShoppingCart, Receipt, Wallet, TrendingUp, Pencil, X, Archive, RotateCcw } from "lucide-react";
import { CurrencyInput } from "@/components/CurrencyInput";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [report, setReport] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [users, setUsers] = useState([]);
  const [cart, setCart] = useState([]);
  const [q, setQ] = useState("");
  const [metode, setMetode] = useState("cash");
  const [diskon, setDiskon] = useState(0);
  const [pelanggan, setPelanggan] = useState("");
  const [posOpen, setPosOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [reimForm, setReimForm] = useState({ kategori: "makan", penerima: "", jumlah: 0, catatan: "" });
  const [savingReim, setSavingReim] = useState(false);

  const loadReport = useCallback(async () => {
    const { data } = await api.get(`/events/${id}/report`);
    setReport(data);
  }, [id]);

  const loadInventory = useCallback(async () => {
    const { data } = await api.get("/inventory");
    setInventory(data);
  }, []);

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch { /* staff cannot list users */ }
  }, [isAdmin]);

  useEffect(() => {
    loadReport();
    loadInventory();
    loadUsers();
  }, [loadReport, loadInventory, loadUsers]);

  const addToCart = (item) => {
    if (item.stok_qty <= 0) return toast.error("Stok habis");
    setCart((c) => {
      const ex = c.find((x) => x.inventory_id === item.id);
      if (ex) {
        if (ex.qty >= item.stok_qty) {
          toast.error("Melebihi stok tersedia");
          return c;
        }
        return c.map((x) => (x.inventory_id === item.id ? { ...x, qty: x.qty + 1 } : x));
      }
      return [...c, { inventory_id: item.id, nama_produk: item.nama_produk, qty: 1, harga_jual: item.harga_jual, harga_beli: item.harga_beli, max: item.stok_qty }];
    });
  };

  const updateQty = (invId, delta) => {
    setCart((c) =>
      c
        .map((x) => (x.inventory_id === invId ? { ...x, qty: Math.min(Math.max(x.qty + delta, 0), x.max) } : x))
        .filter((x) => x.qty > 0)
    );
  };

  const setPrice = (invId, price) => {
    setCart((c) => c.map((x) => (x.inventory_id === invId ? { ...x, harga_jual: Number(price) } : x)));
  };

  const subtotal = cart.reduce((a, b) => a + b.harga_jual * b.qty, 0);
  const total = subtotal - Number(diskon || 0);

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Keranjang kosong");
    try {
      await api.post("/sales", {
        event_id: id,
        items: cart.map((c) => ({ inventory_id: c.inventory_id, nama_produk: c.nama_produk, qty: c.qty, harga_jual: c.harga_jual, harga_beli: c.harga_beli })),
        metode_pembayaran: metode,
        diskon: Number(diskon || 0),
        nama_pelanggan: pelanggan,
      });
      toast.success("Transaksi tercatat");
      setCart([]);
      setDiskon(0);
      setPelanggan("");
      setPosOpen(false);
      loadReport();
      loadInventory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mencatat transaksi");
    }
  };

  const delSale = async (saleId) => {
    if (!window.confirm("Hapus transaksi ini? Stok akan dikembalikan.")) return;
    await api.delete(`/sales/${saleId}`);
    toast.success("Transaksi dihapus");
    loadReport();
    loadInventory();
  };

  const openEdit = () => {
    setEditForm({
      nama_event: ev.nama_event || "",
      lokasi: ev.lokasi || "",
      tanggal_mulai: ev.tanggal_mulai || "",
      tanggal_selesai: ev.tanggal_selesai || "",
      biaya_transport: ev.biaya_transport || 0,
      biaya_booth: ev.biaya_booth || 0,
      biaya_lain: ev.biaya_lain || 0,
      status: ev.status || "active",
      catatan: ev.catatan || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/events/${id}`, editForm);
      toast.success("Event diperbarui");
      setEditOpen(false);
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSavingEdit(false);
    }
  };

  const addReimburse = async () => {
    if (!reimForm.jumlah || reimForm.jumlah <= 0) return toast.error("Nominal reimburse harus diisi");
    setSavingReim(true);
    try {
      await api.post(`/events/${id}/reimbursements`, reimForm);
      toast.success("Reimburse ditambahkan");
      setReimForm({ kategori: "makan", penerima: "", jumlah: 0, catatan: "" });
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menambah reimburse");
    } finally {
      setSavingReim(false);
    }
  };

  const delReimburse = async (rid) => {
    if (!window.confirm("Hapus reimburse ini?")) return;
    await api.delete(`/reimbursements/${rid}`);
    toast.success("Reimburse dihapus");
    loadReport();
  };

  const toggleStatus = async () => {
    const next = ev.status === "active" ? "completed" : "active";
    const msg = next === "completed"
      ? "Arsipkan event ini? Status berubah menjadi Selesai. Data penjualan, reimburse, dan trade tetap tersimpan."
      : "Aktifkan kembali event ini?";
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/events/${id}/status`, { status: next });
      toast.success(next === "completed" ? "Event diarsipkan (Selesai)" : "Event diaktifkan kembali");
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengubah status");
    }
  };

  if (!report) return <div className="flex items-center justify-center h-96 text-slate-500 font-mono text-sm">Memuat...</div>;

  const r = report.ringkasan;
  const ev = report.event;
  const filtered = inventory.filter((i) => i.nama_produk.toLowerCase().includes(q.toLowerCase()));

  const inputCls = "w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm focus:outline-none focus:border-indigo-500 transition";

  return (
    <div>
      <button onClick={() => navigate("/events")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-4 transition" data-testid="back-to-events">
        <ArrowLeft className="w-4 h-4" /> Kembali ke daftar event
      </button>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-indigo-400 mb-1.5">{ev.lokasi} • {ev.status === "active" ? "Aktif" : "Selesai"}</p>
          <h1 className="font-heading text-3xl font-extrabold text-white">{ev.nama_event}</h1>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button data-testid="edit-event-button" onClick={openEdit} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold transition border border-slate-700">
              <Pencil className="w-4 h-4" /> Edit Event
            </button>
          )}
          {isAdmin && (
            <button data-testid="toggle-event-status-button" onClick={toggleStatus} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition border ${ev.status === "active" ? "bg-slate-800 hover:bg-amber-900/40 text-amber-300 border-amber-700/40" : "bg-slate-800 hover:bg-emerald-900/40 text-emerald-300 border-emerald-700/40"}`}>
              {ev.status === "active" ? (<><Archive className="w-4 h-4" /> Arsipkan</>) : (<><RotateCcw className="w-4 h-4" /> Aktifkan</>)}
            </button>
          )}
          <button data-testid="open-pos-button" onClick={() => setPosOpen(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition shadow-lg shadow-indigo-600/30">
            <ShoppingCart className="w-4 h-4" /> Catat Penjualan
          </button>
        </div>
      </div>

      {/* P&L summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rounded-xl border border-slate-800 bg-[#131822] p-4">
          <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-emerald-400" /><p className="text-xs font-mono uppercase text-slate-500">Total Penjualan</p></div>
          <p className="font-mono text-xl font-black text-white" data-testid="report-total-penjualan">{formatRupiah(r.total_penjualan)}</p>
          <p className="text-xs text-slate-500 mt-1">{r.jumlah_transaksi} transaksi</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#131822] p-4">
          <div className="flex items-center gap-2 mb-2"><Wallet className="w-4 h-4 text-amber-400" /><p className="text-xs font-mono uppercase text-slate-500">Modal Barang</p></div>
          <p className="font-mono text-xl font-black text-white">{formatRupiah(r.total_modal)}</p>
          <p className="text-xs text-slate-500 mt-1">HPP terjual</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#131822] p-4">
          <div className="flex items-center gap-2 mb-2"><Receipt className="w-4 h-4 text-rose-400" /><p className="text-xs font-mono uppercase text-slate-500">Biaya Operasional</p></div>
          <p className="font-mono text-xl font-black text-rose-300">{formatRupiah(r.biaya_operasional)}</p>
          <p className="text-xs text-slate-500 mt-1">Transport+Booth+Lain+Reimburse</p>
        </div>
        <div className={`rounded-xl border p-4 ${r.laba_bersih >= 0 ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`}>
          <div className="flex items-center gap-2 mb-2"><TrendingUp className={`w-4 h-4 ${r.laba_bersih >= 0 ? "text-emerald-400" : "text-rose-400"}`} /><p className="text-xs font-mono uppercase text-slate-500">Laba Bersih</p></div>
          <p className={`font-mono text-xl font-black ${r.laba_bersih >= 0 ? "text-emerald-300" : "text-rose-300"}`} data-testid="report-laba-bersih">{formatRupiah(r.laba_bersih)}</p>
          <p className="text-xs text-slate-500 mt-1">Laba kotor {formatRupiah(r.laba_kotor)}</p>
        </div>
      </div>

      {/* Payment method breakdown */}
      <div className="rounded-xl border border-slate-800 bg-[#131822] p-5 mb-6">
        <h3 className="font-heading font-semibold text-white mb-3">Penjualan per Metode Bayar</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex justify-between px-4 py-3 rounded-lg bg-slate-950/40" data-testid="metode-cash"><span className="text-sm text-emerald-300">Cash</span><span className="font-mono text-sm text-white">{formatRupiah((r.by_metode || {}).cash || 0)}</span></div>
          <div className="flex justify-between px-4 py-3 rounded-lg bg-slate-950/40" data-testid="metode-qris"><span className="text-sm text-indigo-300">QRIS</span><span className="font-mono text-sm text-white">{formatRupiah((r.by_metode || {}).qris || 0)}</span></div>
          <div className="flex justify-between px-4 py-3 rounded-lg bg-slate-950/40" data-testid="metode-transfer"><span className="text-sm text-amber-300">Transfer</span><span className="font-mono text-sm text-white">{formatRupiah((r.by_metode || {}).transfer || 0)}</span></div>
        </div>
      </div>

      {/* Cost breakdown */}
      <div className="rounded-xl border border-slate-800 bg-[#131822] p-5 mb-6">
        <h3 className="font-heading font-semibold text-white mb-3">Rincian Biaya Event</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex justify-between px-4 py-3 rounded-lg bg-slate-950/40"><span className="text-sm text-slate-400">Transport</span><span className="font-mono text-sm text-white">{formatRupiah(r.biaya_transport)}</span></div>
          <div className="flex justify-between px-4 py-3 rounded-lg bg-slate-950/40"><span className="text-sm text-slate-400">Booth</span><span className="font-mono text-sm text-white">{formatRupiah(r.biaya_booth)}</span></div>
          <div className="flex justify-between px-4 py-3 rounded-lg bg-slate-950/40"><span className="text-sm text-slate-400">Lain-lain</span><span className="font-mono text-sm text-white">{formatRupiah(r.biaya_lain)}</span></div>
          <div className="flex justify-between px-4 py-3 rounded-lg bg-slate-950/40"><span className="text-sm text-slate-400">Reimburse</span><span className="font-mono text-sm text-white">{formatRupiah(r.total_reimburse)}</span></div>
        </div>
      </div>

      {/* Reimbursement management */}
      <div className="rounded-xl border border-slate-800 bg-[#131822] p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-semibold text-white">Reimburse / Penggantian Biaya</h3>
            <p className="text-xs text-slate-500 mt-0.5">Catat penggantian biaya (makan, transport, dll) beserta penerima transfer</p>
          </div>
          <span className="font-mono text-sm text-rose-300">Total {formatRupiah(r.total_reimburse)}</span>
        </div>

        {isAdmin && (
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-4" data-testid="reimburse-form">
            <select value={reimForm.kategori} onChange={(e) => setReimForm({ ...reimForm, kategori: e.target.value })} className={`${inputCls} sm:col-span-2`} data-testid="reimburse-kategori">
              <option value="makan">Makan</option>
              <option value="transport">Transport</option>
              <option value="akomodasi">Akomodasi</option>
              <option value="lain">Lain-lain</option>
            </select>
            <select value={reimForm.penerima} onChange={(e) => setReimForm({ ...reimForm, penerima: e.target.value })} className={`${inputCls} sm:col-span-3`} data-testid="reimburse-penerima">
              <option value="">Pilih staff...</option>
              {users.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
            </select>
            <div className="sm:col-span-3">
              <CurrencyInput value={reimForm.jumlah} onChange={(v) => setReimForm({ ...reimForm, jumlah: v })} placeholder="Jumlah" className={inputCls} data-testid="reimburse-jumlah" />
            </div>
            <input value={reimForm.catatan} onChange={(e) => setReimForm({ ...reimForm, catatan: e.target.value })} placeholder="Catatan (opsional)" className={`${inputCls} sm:col-span-2`} data-testid="reimburse-catatan" />
            <button onClick={addReimburse} disabled={savingReim} className="sm:col-span-2 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition disabled:opacity-60" data-testid="add-reimburse-button">
              <Plus className="w-4 h-4" /> Tambah
            </button>
          </div>
        )}

        {report.reimbursements.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">Belum ada reimburse tercatat.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reimburse-table">
              <thead>
                <tr className="text-xs font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="text-left px-4 py-2">Kategori</th>
                  <th className="text-left px-4 py-2">Ditransfer ke</th>
                  <th className="text-left px-4 py-2">Catatan</th>
                  <th className="text-right px-4 py-2">Jumlah</th>
                  {isAdmin && <th className="px-4 py-2"></th>}
                </tr>
              </thead>
              <tbody>
                {report.reimbursements.map((rm) => (
                  <tr key={rm.id} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition">
                    <td className="px-4 py-2.5"><span className="capitalize text-slate-300">{rm.kategori}</span></td>
                    <td className="px-4 py-2.5 text-white">{rm.penerima || "-"}</td>
                    <td className="px-4 py-2.5 text-slate-400">{rm.catatan || "-"}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-rose-300">{formatRupiah(rm.jumlah)}</td>
                    {isAdmin && (
                      <td className="px-4 py-2.5 text-right">
                        <button onClick={() => delReimburse(rm.id)} data-testid={`delete-reimburse-${rm.id}`} className="text-slate-500 hover:text-rose-400 transition"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sales list */}
      <div className="rounded-xl border border-slate-800 bg-[#131822] overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800">
          <h3 className="font-heading font-semibold text-white">Riwayat Transaksi</h3>
        </div>
        {report.sales.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">Belum ada transaksi di event ini.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sales-table">
              <thead>
                <tr className="text-xs font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="text-left px-5 py-3">Waktu</th>
                  <th className="text-left px-5 py-3">Item</th>
                  <th className="text-left px-5 py-3">Pelanggan</th>
                  <th className="text-left px-5 py-3">Bayar</th>
                  <th className="text-right px-5 py-3">Total</th>
                  <th className="text-right px-5 py-3">Laba</th>
                  {isAdmin && <th className="px-5 py-3"></th>}
                </tr>
              </thead>
              <tbody>
                {report.sales.map((s) => (
                  <tr key={s.id} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition">
                    <td className="px-5 py-3 text-slate-400 font-mono text-xs">{new Date(s.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-5 py-3 text-slate-300">{s.items.map((i) => `${i.nama_produk} ×${i.qty}`).join(", ")}</td>
                    <td className="px-5 py-3 text-slate-400">{s.nama_pelanggan || "-"}</td>
                    <td className="px-5 py-3"><span className="text-xs uppercase font-mono text-slate-400">{s.metode_pembayaran}</span></td>
                    <td className="px-5 py-3 text-right font-mono text-white">{formatRupiah(s.total)}</td>
                    <td className="px-5 py-3 text-right font-mono text-emerald-300">{formatRupiah(s.laba)}</td>
                    {isAdmin && (
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => delSale(s.id)} data-testid={`delete-sale-${s.id}`} className="text-slate-500 hover:text-rose-400 transition"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* POS drawer */}
      {posOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setPosOpen(false)} />
          <div className="relative z-10 w-full max-w-md bg-[#0F131C] border-l border-slate-800 h-full flex flex-col shadow-2xl">
            <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="font-heading font-bold text-white flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-indigo-400" /> Catat Penjualan</h3>
              <button onClick={() => setPosOpen(false)} className="text-slate-500 hover:text-white text-sm">Tutup</button>
            </div>

            <div className="p-4 border-b border-slate-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input data-testid="pos-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk untuk ditambahkan..." className={`${inputCls} pl-9`} />
              </div>
              <div className="mt-3 max-h-40 overflow-y-auto space-y-1.5">
                {filtered.slice(0, 20).map((it) => {
                  const k = KATEGORI[it.kategori] || KATEGORI.sealed;
                  return (
                    <button key={it.id} data-testid={`pos-add-${it.id}`} onClick={() => addToCart(it)} disabled={it.stok_qty <= 0} className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-slate-900/60 hover:bg-slate-800 transition text-left disabled:opacity-40">
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{it.nama_produk}</p>
                        <p className={`text-[10px] ${k.cls.split(" ")[1]}`}>{k.label} • Stok {it.stok_qty}</p>
                      </div>
                      <span className="font-mono text-xs text-emerald-300 shrink-0 ml-2">{formatRupiah(it.harga_jual)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="pos-cart">
              {cart.length === 0 ? (
                <p className="text-center text-sm text-slate-600 py-10">Keranjang kosong. Cari & pilih produk di atas.</p>
              ) : (
                cart.map((c) => (
                  <div key={c.inventory_id} className="rounded-lg border border-slate-800 bg-slate-900/50 p-3">
                    <div className="flex justify-between items-start gap-2">
                      <p className="text-sm text-white flex-1">{c.nama_produk}</p>
                      <button onClick={() => updateQty(c.inventory_id, -c.qty)} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQty(c.inventory_id, -1)} className="w-6 h-6 rounded bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-white"><Minus className="w-3 h-3" /></button>
                        <span className="font-mono text-sm text-white w-6 text-center">{c.qty}</span>
                        <button onClick={() => updateQty(c.inventory_id, 1)} className="w-6 h-6 rounded bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-white"><Plus className="w-3 h-3" /></button>
                      </div>
                      <input type="number" value={c.harga_jual} onChange={(e) => setPrice(c.inventory_id, e.target.value)} className="w-28 px-2 py-1 rounded bg-slate-950/60 border border-slate-700 text-white text-sm text-right font-mono focus:outline-none focus:border-indigo-500" />
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="border-t border-slate-800 p-4 space-y-3">
              <input value={pelanggan} onChange={(e) => setPelanggan(e.target.value)} placeholder="Nama pelanggan (opsional)" className={inputCls} data-testid="pos-customer" />
              <div className="grid grid-cols-2 gap-3">
                <select value={metode} onChange={(e) => setMetode(e.target.value)} className={inputCls} data-testid="pos-payment">
                  <option value="cash">Cash</option>
                  <option value="qris">QRIS</option>
                  <option value="transfer">Transfer</option>
                </select>
                <input type="number" value={diskon} onChange={(e) => setDiskon(e.target.value)} placeholder="Diskon" className={inputCls} data-testid="pos-discount" />
              </div>
              <div className="flex justify-between text-sm text-slate-400"><span>Subtotal</span><span className="font-mono">{formatRupiah(subtotal)}</span></div>
              <div className="flex justify-between text-base font-bold text-white"><span>Total</span><span className="font-mono text-emerald-300">{formatRupiah(total)}</span></div>
              <button data-testid="checkout-button" onClick={checkout} className="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold transition shadow-lg shadow-indigo-600/30">
                Simpan Transaksi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit event modal */}
      {editOpen && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditOpen(false)} />
          <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-800 bg-[#131822] p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl font-bold text-white">Edit Detail Event</h2>
              <button onClick={() => setEditOpen(false)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-400 mb-1.5">Nama Event</label>
                <input className={inputCls} value={editForm.nama_event} onChange={(e) => setEditForm({ ...editForm, nama_event: e.target.value })} data-testid="edit-event-nama" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-400 mb-1.5">Lokasi</label>
                <input className={inputCls} value={editForm.lokasi} onChange={(e) => setEditForm({ ...editForm, lokasi: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Tanggal Mulai</label>
                <input type="date" className={inputCls} value={editForm.tanggal_mulai} onChange={(e) => setEditForm({ ...editForm, tanggal_mulai: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Tanggal Selesai</label>
                <input type="date" className={inputCls} value={editForm.tanggal_selesai} onChange={(e) => setEditForm({ ...editForm, tanggal_selesai: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Biaya Transport</label>
                <CurrencyInput value={editForm.biaya_transport} onChange={(v) => setEditForm({ ...editForm, biaya_transport: v })} className={inputCls} data-testid="edit-event-transport" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Biaya Booth</label>
                <CurrencyInput value={editForm.biaya_booth} onChange={(v) => setEditForm({ ...editForm, biaya_booth: v })} className={inputCls} data-testid="edit-event-booth" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Biaya Lain-lain</label>
                <CurrencyInput value={editForm.biaya_lain} onChange={(v) => setEditForm({ ...editForm, biaya_lain: v })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Status</label>
                <select className={inputCls} value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} data-testid="edit-event-status">
                  <option value="active">Aktif</option>
                  <option value="completed">Selesai</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditOpen(false)} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition">Batal</button>
              <button onClick={saveEdit} disabled={savingEdit} className="px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white transition disabled:opacity-60" data-testid="save-edit-event-button">
                {savingEdit ? "Menyimpan..." : "Simpan Perubahan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
